import React, { useState, useEffect, useCallback } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const MAX_RECURSIVE_PASSES = 10;

const decodeOnce = (text) => decodeURIComponent(text);

const decodeRecursive = (text) => {
  let current = text;
  let passes = 0;
  for (let i = 0; i < MAX_RECURSIVE_PASSES; i += 1) {
    let next;
    try {
      next = decodeOnce(current);
    } catch {
      if (passes === 0) throw new Error("decoding_error");
      return { result: current, passes };
    }
    if (next === current) return { result: current, passes };
    current = next;
    passes += 1;
    if (!/%[0-9A-Fa-f]{2}/.test(current)) break;
  }
  return { result: current, passes };
};

const URLDecoder = () => {
  const [input, setInput] = useState("https%253A%252F%252Fexample.com%252Fsearch%253Fquery%253Dhello%2520world");
  const [decoded, setDecoded] = useState("");
  const [passes, setPasses] = useState(0);
  const [recursive, setRecursive] = useState(true);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/url-decode/share/${id}`)
        .then((res) => {
          const data = res.data;
          if (data.input && data.result) {
            setInput(data.input);
            handleLocalDecode(data.input, recursive);
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, []);

  const handleLocalDecode = (text, useRecursive) => {
    try {
      if (useRecursive) {
        const { result, passes: p } = decodeRecursive(text);
        setDecoded(result);
        setPasses(p);
        return result;
      }
      const decodedText = decodeOnce(text);
      setDecoded(decodedText);
      setPasses(1);
      return decodedText;
    } catch (error) {
      toast.error(t('decoding_error'));
      return "";
    }
  };

  const handleDecode = useCallback(() => {
    if (!input.trim()) {
      toast.error(t('enter_encoded_url'));
      return;
    }
    handleLocalDecode(input, recursive);
  }, [input, recursive, t]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      handleDecode();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleDecode]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInput(newValue);
  };

  const handleClear = () => {
    setInput("");
    setDecoded("");
    setPasses(0);
  };

  const handleCopy = async () => {
    if (!decoded) {
      toast.error(t('nothing_to_copy'));
      return;
    }
    try {
      await navigator.clipboard.writeText(decoded);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    if (!input || !decoded) {
      toast.error(t('nothing_to_share'));
      return;
    }
    try {
      const response = await api.post(`/url-decode/share`, { input, result: decoded });
      const data = response.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/url-decoder?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t('url_decoder_title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('url_decoder_intro')}</p>
            </div>

            <textarea
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
              placeholder={t('enter_encoded_url_placeholder')}
              value={input}
              onChange={handleInputChange}
            />

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={recursive}
                onChange={(e) => setRecursive(e.target.checked)}
                className="h-4 w-4 mt-0.5"
              />
              <span>
                <span className="font-medium">{t('url_decoder_recursive_label')}</span>
                <span className="block text-xs text-gray-500">
                  {t('url_decoder_recursive_help')}
                </span>
              </span>
            </label>

            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap gap-2">
                <StandardButton
                  onClick={handleDecode}
                  type="secondary"
                  className="flex-1 min-w-[8rem]"
                  title={t('url_decoder_shortcut_hint')}
                >
                  {t('decode_button')}
                </StandardButton>
                <StandardButton
                  onClick={handleClear}
                  type="secondary"
                  variant="outlined"
                  disabled={!input && !decoded}
                >
                  {t('url_decoder_clear')}
                </StandardButton>
              </div>
              <span className="text-xs text-gray-500">
                {t('url_decoder_shortcut_hint')}
              </span>
            </div>

            {decoded && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-indigo-700">{t('decoded_output')}:</strong>
                  {recursive && passes > 0 && (
                    <span className="text-xs text-gray-600">
                      {t('url_decoder_passes', { count: passes })}
                    </span>
                  )}
                </div>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">{decoded}</p>
                <div className="mt-4 flex gap-3">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>

                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default URLDecoder;
